import { module } from 'modujs';
import { shuffle } from '../utils/array';
import vertexShader from '../shaders/distortionVertex';
import fragmentShader from '../shaders/distortionFragment';
import fragmentAntiAliasShader from '../shaders/fragmentAntiAlias.js';
import { lerp } from '../utils/maths';

import { Renderer, Transform, Post, Camera, Texture, Vec2, Plane, Program, Mesh, Color } from 'ogl';

const CLASS = {
    LOADING: 'is-loading'
}

export default class extends module {
    constructor(m) {
        super(m);

        this.el.classList.add(CLASS.LOADING);

        this.texturesSrc = this.getData('textures').split(',');
        this.texturesSrc = shuffle(this.texturesSrc);
        this.displacementSrc = this.getData('displacement');
        this.gap = this.getData('gap');

        this.windowWidth = window.innerWidth;
        this.windowHeight = window.innerHeight;
        this.isMobile = window.isMobile;

        this.events = {
            click: 'click'
        }

        // El and webgl plane BCR
        this.BCR = this.$('wrap')[0].getBoundingClientRect();
        this.planeBCR = {
            width: 1,
            height: 1,
            x: 0,
            y: 0
        }

        // Useful booleans
        this.inView = true;
        this.isLoaded = false;
        this.isRenderable = false;
        this.isTransitionning = false;

        // Positions
        this.displacementPosition = new Vec2(-0.5, -0.5);

        this.settings = {
            factor: 0,
            lerpedFactor: 0,
            factorAim: this.getData('factor'),
            scale: 0
        }

        this.index = 0;

    }

    init() {

        this.$wrap = this.$('wrap')[0];

        // Init ogl renderer
        this.renderer = new Renderer({ dpr: 1, antialias: true, alpha: true, premultiplyAlpha: false });
        this.renderer.setSize(this.BCR.width, this.BCR.height);

        this.gl = this.renderer.gl;
        const clearColor = new Color(this.getData('clear-color'));
        this.gl.clearColor(clearColor[0], clearColor[1], clearColor[2], clearColor[3]);
        this.$wrap.appendChild(this.gl.canvas);

        this.resizeBind = this.resize.bind(this);
        window.addEventListener('resize', this.resizeBind);

        gsap.delayedCall(0.3, () => {
            this.initScene();
            this.initCamera();
            this.initShape();
        });

        this.autoplay = setTimeout(this.click.bind(this), 5000);
    }

    click() {
        if (this.isTransitionning || this.texturesSrc.length === 1) return;
        clearTimeout(this.autoplay);

        const savedPosition = {};

        savedPosition.x = this.displacementPosition.x;
        savedPosition.y = this.displacementPosition.y;

        this.isTransitionning = true;

        this.settings.factor = this.settings.factorAim;

        gsap.fromTo(
            this.displacementPosition,
            {
                y: -0.8
            },
            {
                duration: 1.6,
                y: 1,
                ease: "power1.inOut",
                onComplete: () => {
                    this.settings.factor = 0;
                    this.settings.lerpedFactor = 0;

                    this.displacementPosition.x = savedPosition.x;
                    this.displacementPosition.y = savedPosition.y;
                    this.isTransitionning = false;

                    this.autoplay = setTimeout(this.click.bind(this), 5000);
                }
            }
        );

        let oldIndex = this.index;

        if (this.index < this.slides.length - 1) {
            this.index++
        } else {
            this.index = 0
        }

        this.slides.forEach((slide, index) => {
            if (index === this.index) {
                slide.program.uniforms.isOut.value = 1;
                slide.program.uniforms.maskPosition.value.y = 1;

                gsap.to(slide.program.uniforms.maskPosition.value, {
                    duration: 1.6,
                    y: 0,
                    ease: "power2.inOut"
                });
            } else if (index === oldIndex) {
                slide.program.uniforms.isOut.value = 0;
                slide.program.uniforms.maskPosition.value.y = 1;

                gsap.to(slide.program.uniforms.maskPosition.value, {
                    duration: 1.6,
                    y: 0,
                    ease: "power2.inOut"
                });
            }
        });

    }

    initScene() {
        this.scene = new Transform();
    }

    initCamera() {
        this.fov = 75;
        this.camera = new Camera(this.glElement, { fov: this.fov });
        this.camera.position.set(0, 0, 1);
    }

    initShape() {

        const displacementTexture = new Texture(this.gl, { minFilter: this.gl.LINEAR });
        const displacementImg = new Image();
        displacementImg.src = this.displacementSrc;

        this.slides = new Array();
        this.loadedImages = 0;

        displacementImg.onload = () => {
            displacementTexture.image = displacementImg;

            this.texturesSrc.forEach((textureSrc, index) => {
                let img = new Image();
                let texture = new Texture(this.gl, { minFilter: this.gl.LINEAR });
                img.src = textureSrc;

                img.onload = () => {
                    this.loadedImages++;
                    texture.image = img;

                    let program = new Program(this.gl, {
                        vertex: vertexShader,
                        fragment: fragmentShader,
                        transparent: true,
                        depthWrite: false,
                        uniforms: {
                            displacementTexture: { value: displacementTexture },
                            displacement: { value: this.displacementPosition },
                            texture: { value: texture },
                            factor: { value: this.settings.factor },
                            maskPosition: { value: new Vec2(0, 0) },
                            isOut: { value: index == 0 ? 1 : 0 }
                        }
                    });

                    let mesh = new Mesh(this.gl, { geometry: this.geometry, program: program });

                    this.slides[index] = {
                        mesh: mesh,
                        program: program
                    }
                    mesh.setParent(this.scene);

                    if (this.loadedImages === this.slides.length) {

                        this.updateSize();
                        this.isLoaded = true;
                        this.el.classList.remove(CLASS.LOADING);

                        // this.render();
                        this.resize();
                    }
                };
            });

        };
    }


    calculateUnitSize(distance) {
        const vFov = this.fov * Math.PI / 180;
        const height = (2 * Math.tan(vFov / 2) * distance);
        const width = height * this.camera.aspect;

        return {
            width,
            height
        }
    }

    updateSize() {
        this.camUnit = this.calculateUnitSize(this.camera.position.z);

        // Set size @update
        this.planeBCR.width = this.camUnit.width - (this.camUnit.width * (this.gap / 100));
        this.planeBCR.height = this.planeBCR.width / this.camera.aspect;

        this.geometry = new Plane(this.gl, { width: this.planeBCR.width, height: this.planeBCR.height, widthSegments: 100, heightSegments: 100 });

        this.slides.forEach(slide => {
            slide.mesh.geometry = this.geometry;
        });

        this.gl.canvas.style.width = `${this.BCR.width}px`;
        this.gl.canvas.style.height = `${this.BCR.height}px`;

    }

    trigger(e) {
        if (this.isLoaded) {
            if (e.way === 'enter') {
                cancelAnimationFrame(this.raf);
                this.isRenderable = true;
                this.render();
            } else {
                this.isRenderable = false;
                cancelAnimationFrame(this.raf);
            }
        } else {
            if (e.way === 'enter') {
                this.isRenderable = true;
            } else {
                this.isRenderable = false;
            }
        }
    }

    formatPosition(param) {
        param.obj.x = -(param.x) + 0.5;
        param.obj.y = param.y - 0.5
    }

    render(t) {
        this.raf = requestAnimationFrame((t) => this.render(t));

        if (this.isLoaded && this.isRenderable) {
            if (Math.abs(this.settings.lerpedFactor - this.settings.factor) > 0.01) {
                this.settings.lerpedFactor = lerp(this.settings.lerpedFactor, this.settings.factor, 0.07);
            }

            this.slides.forEach((slide, index) => {
                slide.program.uniforms.displacement.value = this.displacementPosition;
                slide.program.uniforms.factor.value = this.settings.lerpedFactor;
            });
        }

        if (!this.isMobile) {
            this.renderer.render({ scene: this.scene, camera: this.camera });

        } else {
            this.renderer.render({ scene: this.scene, camera: this.camera });
        }
    }

    resize() {
        const newBCR = this.$wrap.getBoundingClientRect()
        if (this.BCR && this.BCR.top == newBCR.top && this.BCR.height == newBCR.height) return
        this.BCR = newBCR

        this.windowWidth = window.innerWidth;
        this.windowHeight = window.innerHeight;

        this.renderer.setSize(this.BCR.width, this.BCR.height);
        this.camera.perspective({ aspect: this.gl.canvas.width / this.gl.canvas.height });

        this.updateSize();
    }

    destroy() {
        super.destroy();
        cancelAnimationFrame(this.raf);
        window.removeEventListener('resize', this.resizeBind);
        clearTimeout(this.autoplay);
    }
}
